import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  CurrencyInput,
  DataGrid,
  Divider,
  FormControl,
  Grid,
  TextField,
  TextFielDecimalNumbers,
  Tooltip,
  Typography,
} from "../../../../components";
import { InputErros, value } from "../../../../helpers";
import shortid from "shortid";
import {
  formatPercentage,
  formatPrice,
  unformatPrice,
} from "../../../../utils";
import { notification } from "../../../../services";

const RateioPlanoFinanceiro = ({
  contaPagar,
  rateiosPlanoFinanceiroList,
  setRateiosPlanoFinanceiroList,
  rateiosCentroCustoList,
  setRateiosCentroCustoList,
  planoFinanceiroList = [],
  centroCustoList = [],
}) => {
  const [rateioPlanoFinanceiroItem, setRateioPlanoFinanceiroItem] = useState(
    {},
  );
  const [rateioCentroCustoItem, setRateioCentroCustoItem] = useState({
    percentual: 100,
  });
  // const [isEditing, setIsEditing] = useState(false);
  const [totalRateio, setTotalRateio] = useState(0);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  const colunasPlanoFinanceiro = [
    {
      field: "planoFinanceiroId",
      headerName: "Plano Financeiro",
      flex: 350,
      valueGetter: (params) =>
        value.autoComplete(planoFinanceiroList, params.value)?.descricao,
    },
    {
      field: "valor",
      headerName: "Valor (R$)",
      flex: 230,
      valueGetter: (params) => formatPrice(parseFloat(params.value)),
    },
    ...rateiosCentroCustoList.map((custo, index) => ({
      field: `custo-${index}`,
      headerName: value.autoComplete(centroCustoList, custo.centroCustoId)
        ?.descricao,
      flex: 120,
      valueGetter: (params) =>
        formatPrice((parseFloat(custo.percentual) / 100) * params.row.valor),
    })),
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      flex: 100,
      align: "center",
      headerAlign: "center",
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <div className="d-flex gap-2">
            <Tooltip title="Editar Item" placement="bottom">
              <Button
                variant="contained"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditItemPlanoFinanceiro(params.id);
                }}
              >
                <i
                  className="ph-bold ph-pencil"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            </Tooltip>
            <Tooltip title="Excluir um item" placement="bottom">
              <Button
                variant="contained"
                color="error"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoverRateioPlanoFinanceiro(params.id);
                }}
              >
                <i
                  className="ph-bold ph-trash"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const colunasCentroCusto = [
    {
      field: "centroCustoId",
      headerName: "Centro de Custo",
      flex: 450,
      valueGetter: (params) =>
        value.autoComplete(centroCustoList, params.value)?.descricao,
    },
    {
      field: "percentual",
      headerName: "Percentual (%)",
      flex: 250,
      valueGetter: (params) => formatPercentage(parseFloat(params.value), 2),
    },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      flex: 100,
      align: "center",
      headerAlign: "center",
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <div className="d-flex gap-2">
            <Tooltip title="Editar Item" placement="bottom">
              <Button
                variant="contained"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditItemCentroCusto(params.id);
                }}
              >
                <i
                  className="ph-bold ph-pencil"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            </Tooltip>
            <Tooltip title="Excluir um item" placement="bottom">
              <Button
                variant="contained"
                color="error"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoverRateioCentroCusto(params.id);
                }}
              >
                <i
                  className="ph-bold ph-trash"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    setTotalRateio(
      rateiosPlanoFinanceiroList.reduce(
        (acc, rateio) => acc + (parseFloat(rateio?.valor) || 0),
        0,
      ),
    );
  }, [rateiosPlanoFinanceiroList]);

  useEffect(() => {
    if (
      !rateiosPlanoFinanceiroList?.length &&
      parseFloat(contaPagar.valor) > 0
    ) {
      const valor = contaPagar && contaPagar.valor ? contaPagar.valor : 0;
      setRateioPlanoFinanceiroItem((prevState) => ({
        ...prevState,
        valor,
      }));
    }
  }, [contaPagar.valor]);

  const onChangeAutocomplete = (state, setState, name, value) => {
    setState({
      ...state,
      [name]: value ? value.id : undefined,
    });
  };

  function onChangeValor(e) {
    const valor = unformatPrice(e.target.value);
    let valorFormatado = Math.floor(parseFloat(valor) * 100) / 100;

    const totalRateioValid = rateioPlanoFinanceiroItem?.isEditing
      ? totalRateio - rateioPlanoFinanceiroItem?.valorAnterior
      : totalRateio;
    const valorTotal = totalRateioValid + valorFormatado;
    if (valorTotal > parseFloat(contaPagar?.valorTotal || 0)) {
      const valorRestante =
        parseFloat(contaPagar?.valorTotal || 0) - totalRateioValid;
      valorFormatado = valorRestante > 0 ? valorRestante : valorFormatado;
    }
    setRateioPlanoFinanceiroItem((prevState) => ({
      ...prevState,
      valor: valorFormatado,
    }));
  }

  function onChangePercentual(e) {
    const valor = unformatPrice(e.target.value);
    let valorFormatado = Math.floor(parseFloat(valor) * 100) / 100;
    const totalRateio = rateiosCentroCustoList.reduce(
      (acc, c) => acc + parseFloat(c.percentual),
      0,
    );
    const totalRateioValid = rateioCentroCustoItem?.isEditing
      ? totalRateio - rateioCentroCustoItem?.percentualAnterior
      : totalRateio;
    const valorTotal = totalRateioValid + valorFormatado;

    if (valorTotal > 100) {
      const valorRestante = 100 - totalRateioValid;
      valorFormatado = valorRestante > 0 ? valorRestante : valorFormatado;
    }

    setRateioCentroCustoItem((prevState) => ({
      ...prevState,
      percentual: valorFormatado,
    }));
  }

  const handleRemoverRateioPlanoFinanceiro = async (id) => {
    const rateioFiltrado = rateiosPlanoFinanceiroList.filter(
      (item) => item.id !== id,
    );
    setRateiosPlanoFinanceiroList(rateioFiltrado);
    const total = rateioFiltrado.reduce(
      (acc, rateio) => acc + (parseFloat(rateio?.valor) || 0),
      0,
    );
    setRateioPlanoFinanceiroItem((prevState) => ({
      ...prevState,
      valor: parseFloat(contaPagar?.valorTotal || 0) - total,
    }));
  };

  const handleRemoverRateioCentroCusto = async (id) => {
    const rateioFiltrado = rateiosCentroCustoList.filter(
      (item) => item.id !== id,
    );
    setRateiosCentroCustoList(rateioFiltrado);
    const total = rateioFiltrado.reduce(
      (acc, rateio) => acc + (parseFloat(rateio?.percentual) || 0),
      0,
    );
    setRateioCentroCustoItem((prevState) => ({
      ...prevState,
      percentual: 100 - total,
    }));
  };

  function onEditItemPlanoFinanceiro(id) {
    const rateio = rateiosPlanoFinanceiroList.find((item) => item.id === id);
    setRateioPlanoFinanceiroItem({
      ...rateio,
      isEditing: true,
      valorAnterior: rateio.valor,
    });
  }

  function onEditItemCentroCusto(id) {
    const rateio = rateiosCentroCustoList.find((item) => item.id === id);
    setRateioCentroCustoItem({
      ...rateio,
      isEditing: true,
      percentualAnterior: rateio.percentual,
      percentual: rateio.percentual,
    });
  }

  function addItemRateioPlanoFinanciero() {
    if (!rateioPlanoFinanceiroItem.planoFinanceiroId) {
      return notification.alertaGenericos("Insira um Plano Financeiro");
    }
    if (
      !rateioPlanoFinanceiroItem.valor &&
      rateioPlanoFinanceiroItem.valor <= 0
    ) {
      return notification.alertaGenericos("Insira um valor");
    }
    if (rateioPlanoFinanceiroItem.isEditing) {
      const itemIndex = rateiosPlanoFinanceiroList.findIndex(
        (rateio) => rateio.id == rateioPlanoFinanceiroItem.id,
      );
      setRateiosPlanoFinanceiroList((prevState) =>
        prevState.map((rateio, i) =>
          i === itemIndex
            ? { ...rateio, ...rateioPlanoFinanceiroItem }
            : rateio,
        ),
      );
      setRateioPlanoFinanceiroItem({
        valor:
          parseFloat(contaPagar?.valorTotal || 0) -
          (totalRateio -
            rateioPlanoFinanceiroItem?.valorAnterior +
            rateioPlanoFinanceiroItem?.valor),
      });
      return;
    }
    setRateiosPlanoFinanceiroList((prevState) => [
      ...prevState,
      { ...rateioPlanoFinanceiroItem, id: shortid.generate() },
    ]);
    setRateioPlanoFinanceiroItem({
      valor:
        parseFloat(contaPagar?.valorTotal || 0) -
        totalRateio -
        rateioPlanoFinanceiroItem.valor,
    });
  }

  function addItemRateioCentroCusto() {
    if (!rateioCentroCustoItem.centroCustoId) {
      return notification.alertaGenericos("Insira um Centro de Custo");
    }
    if (
      !rateioCentroCustoItem.percentual &&
      rateioCentroCustoItem.percentual <= 0
    ) {
      return notification.alertaGenericos("Insira um percentual");
    }
    if (rateioCentroCustoItem.isEditing) {
      const itemIndex = rateiosCentroCustoList.findIndex(
        (rateio) => rateio.id == rateioCentroCustoItem.id,
      );
      setRateiosCentroCustoList((prevState) =>
        prevState.map((rateio, i) =>
          i === itemIndex ? { ...rateio, ...rateioCentroCustoItem } : rateio,
        ),
      );
      const calculo =
        100 -
        rateiosCentroCustoList.reduce(
          (acc, c) => acc + parseFloat(c.percentual),
          0,
        ) -
        rateioCentroCustoItem.percentualAnterior +
        rateioCentroCustoItem.percentual;
      setRateioCentroCustoItem({
        percentual: calculo < 0 ? calculo * -1 : calculo,
      });
      return;
    }
    setRateiosCentroCustoList((prevState) => [
      ...prevState,
      {
        ...rateioCentroCustoItem,
        id: shortid.generate(),
      },
    ]);
    setRateioCentroCustoItem({
      percentual:
        100 -
        (rateiosCentroCustoList.reduce(
          (acc, c) => acc + parseFloat(c.percentual),
          0,
        ) +
          rateioCentroCustoItem.percentual),
    });
  }

  return (
    <>
      <Grid container className="mt-4">
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
            Centro de Custo
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={7.45}>
              <FormControl variant="outlined" margin="normal" fullWidth>
                <Autocomplete
                  required
                  id="centroCustoId"
                  name="centroCustoId"
                  options={centroCustoList.filter(
                    (item) =>
                      !rateiosCentroCustoList.some(
                        (c) => c.centroCustoId === item.id,
                      ),
                  )}
                  autoHighlight
                  getOptionLabel={(option) =>
                    option.descricao
                      ? `${option.codigo}` + ` - ` + `${option.descricao}`
                      : ""
                  }
                  getOptionSelected={(option, value) =>
                    option.descricao === value.descricao
                  }
                  noOptionsText="Sem opções"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Centro de Custo"
                      variant="outlined"
                      error={inputErros.get("centroCustoId")}
                    />
                  )}
                  onChange={(_, newValue) => {
                    onChangeAutocomplete(
                      rateioCentroCustoItem,
                      setRateioCentroCustoItem,
                      "centroCustoId",
                      newValue,
                    );
                  }}
                  value={value.autoComplete(
                    centroCustoList,
                    rateioCentroCustoItem.centroCustoId,
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextFielDecimalNumbers
                id="percentual"
                name="percentual"
                label="Percentual (%)"
                value={rateioCentroCustoItem?.percentual}
                variant={"outlined"}
                onBlur={onChangePercentual}
              />
            </Grid>
            <Grid item xs={0.55} className="mt-4">
              <Button
                variant="contained"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                disabled={
                  !rateioCentroCustoItem?.isEditing &&
                  rateiosCentroCustoList.reduce(
                    (acc, c) => acc + parseFloat(c.percentual),
                    0,
                  ) >= 100
                }
                onClick={() => addItemRateioCentroCusto()}
              >
                <i
                  className="ph-bold ph-plus"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                style={{ zIndex: 1 }}
                rows={rateiosCentroCustoList}
                columns={colunasCentroCusto}
                rowCount={rateiosCentroCustoList.length}
                disableAcoes
              />
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ my: 4, border: "1px solid grey", width: "100%" }} />
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
            Plano Financeiro
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={7.45}>
              <FormControl variant="outlined" margin="normal" fullWidth>
                <Autocomplete
                  required
                  id="planoFinanceiroId"
                  name="planoFinanceiroId"
                  options={planoFinanceiroList.filter(
                    (item) =>
                      !rateiosPlanoFinanceiroList.some(
                        (r) => r.planoFinanceiroId === item.id,
                      ),
                  )}
                  autoHighlight
                  getOptionLabel={(option) =>
                    option.descricao
                      ? `${option.codigo}` + ` - ` + `${option.descricao}`
                      : ""
                  }
                  getOptionSelected={(option, value) =>
                    option.descricao === value.descricao
                  }
                  noOptionsText="Sem opções"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Plano Financeiro"
                      variant="outlined"
                      error={inputErros.get("planoFinanceiroId")}
                    />
                  )}
                  onChange={(_, newValue) => {
                    onChangeAutocomplete(
                      rateioPlanoFinanceiroItem,
                      setRateioPlanoFinanceiroItem,
                      "planoFinanceiroId",
                      newValue,
                    );
                  }}
                  value={value.autoComplete(
                    planoFinanceiroList,
                    rateioPlanoFinanceiroItem?.planoFinanceiroId,
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <CurrencyInput
                id="valor"
                name="valor"
                label="Valor Unitário R$"
                value={rateioPlanoFinanceiroItem?.valor}
                variant={"outlined"}
                onBlur={onChangeValor}
              />
            </Grid>
            <Grid item xs={0.55} className="mt-4">
              <Button
                variant="contained"
                size="small"
                sx={{
                  height: "35px",
                  borderRadius: "20%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                disabled={
                  !rateioPlanoFinanceiroItem?.isEditing &&
                  totalRateio === parseFloat(contaPagar?.valorTotal)
                }
                onClick={() => addItemRateioPlanoFinanciero()}
              >
                <i
                  className="ph-bold ph-plus"
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                style={{ zIndex: 1 }}
                rows={rateiosPlanoFinanceiroList}
                columns={colunasPlanoFinanceiro}
                rowCount={rateiosPlanoFinanceiroList.length}
                disableAcoes
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default RateioPlanoFinanceiro;
